import { useCallback } from 'react';
import {
    atomWithStorage,
    createJSONStorage,
    useAtomCallback,
} from 'jotai/utils';
import type { EnquiryType } from '~/ui-rocco/pages/category-page/GetMatchedModal/enquiryConfig';
import type { UserType } from '~/ui-rocco/pages/category-page/GetMatchedModal/Step1UserType';

/**
 * The wizard's answers minus the attachments. Files cannot survive a
 * JSON round-trip; they ride the social redirect in IndexedDB instead,
 * under {@link GET_MATCHED_GATE_FILE_SESSION}.
 */
export interface GetMatchedGateBriefAnswers {
    enquiryType: EnquiryType | null;
    userType: UserType | null;
    region: string | null;
    projectType: string | null;
    projectScope: string | null;
    /**
     * The product-flow "what kind of product" answer. Submitted via the same
     * `ProfessionalType` DB column as the other variants' "what the user is
     * looking for" answer.
     */
    productCategory: string | null;
    style: string | null;
    budget: string | null;
    description: string;
    professionals: string | null;
    startDate: string | null;
    endDate: string | null;
    email: string;
    buildHubID: number | null;
}

export interface GetMatchedGateBrief {
    answers: GetMatchedGateBriefAnswers;
    hasAttachments: boolean;
    savedAt: number;
}

/** Keyspace for the brief's attachments in `~/utils/file-storage`. */
export const GET_MATCHED_GATE_FILE_SESSION = 'get-matched-gate';

/**
 * A stashed brief older than this is dropped rather than resumed. Consuming a
 * brief fires a real submission on the professional's desk, so an abandoned
 * one must not ambush a user who signs in for an unrelated reason later on.
 */
const BRIEF_TTL_MS = 30 * 60 * 1000;

/**
 * The brief captured before the gate's auth step, persisted so it survives a
 * social round-trip's full reload. Session-scoped for the same reason the
 * other gate intents are: an abandoned brief must not follow the user into a
 * later visit. `getOnInit` because the resume path reads imperatively right
 * after the reload, before anything has subscribed the atom — and with it the
 * storage getter runs at module evaluation, so it must hand the server
 * `undefined` rather than throw.
 */
export const getMatchedGateBriefAtom =
    atomWithStorage<GetMatchedGateBrief | null>(
        'get-matched-gate-brief',
        null,
        createJSONStorage<GetMatchedGateBrief | null>(
            // The runtime tolerates an absent storage — jotai's own default
            // getter returns undefined from its catch — but the sync-storage
            // signature does not admit it, hence the cast.
            () =>
                (typeof window === 'undefined'
                    ? undefined
                    : window.sessionStorage) as Storage,
        ),
        { getOnInit: true },
    );

export interface UseGetMatchedGateBrief {
    /** Stash the brief before the gate takes the surface. */
    save: (
        answers: GetMatchedGateBriefAnswers,
        hasAttachments: boolean,
    ) => void;
    /**
     * Read-once: returns the brief and clears it in the same store write, so
     * with several `GetMatchedModal` instances mounted on one page exactly one
     * resume effect wins the social return. Expired briefs come back `null`.
     */
    take: () => GetMatchedGateBrief | null;
    /** Forget the brief — it must not outlive a dismissal or a landed submit. */
    clear: () => void;
}

export const useGetMatchedGateBrief = (): UseGetMatchedGateBrief => {
    const save = useAtomCallback(
        useCallback(
            (
                _get,
                set,
                answers: GetMatchedGateBriefAnswers,
                hasAttachments: boolean,
            ) => {
                set(getMatchedGateBriefAtom, {
                    answers,
                    hasAttachments,
                    savedAt: Date.now(),
                });
            },
            [],
        ),
    );

    const take = useAtomCallback(
        useCallback((get, set): GetMatchedGateBrief | null => {
            const brief = get(getMatchedGateBriefAtom);
            if (!brief) return null;
            set(getMatchedGateBriefAtom, null);
            if (Date.now() - brief.savedAt > BRIEF_TTL_MS) return null;
            return brief;
        }, []),
    );

    const clear = useAtomCallback(
        useCallback((_get, set) => {
            set(getMatchedGateBriefAtom, null);
        }, []),
    );

    return { save, take, clear };
};
